import { Injectable } from '@angular/core';

const AUTH_KEY = 'auth-token';
const USER_KEY = 'auth-user';
const INDIVIDUAL_ID_KEY = 'individual-id';
const REDIRECT_URL_KEY = 'redirect-url';
const REFRESH_TOKEN_KEY = 'refresh-token';
const USER_EMAIL_KEY = 'user-email';
const USER_ID_KEY = 'user-id';
const PRIMARY_EMAIL_KEY = 'primaryEmail';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor() { }

  public saveAuthInfo(data: any){
    this.addItem(AUTH_KEY, data.access_token);
    window.sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  }

  public getToken(): string | null {
    return this.getItem(AUTH_KEY);
  }

  public getRefreshToken(): string | null {
    return window.sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  public getAuthInfo(): any {
    const authInfo = sessionStorage.getItem('auth-token');
    return authInfo ? JSON.parse(authInfo) : null;
  }

  public saveRedirectUrl(url: string): void {
    window.sessionStorage.removeItem(REDIRECT_URL_KEY);
    window.sessionStorage.setItem(REDIRECT_URL_KEY, url);
    // Also save timestamp to help with debugging session timeouts
    window.sessionStorage.setItem(REDIRECT_URL_KEY + '_timestamp', Date.now().toString());
  }

  public getRedirectUrl(): string | null {
    return window.sessionStorage.getItem(REDIRECT_URL_KEY);
  }

  public clearRedirectUrl(): void {
    window.sessionStorage.removeItem(REDIRECT_URL_KEY);
    window.sessionStorage.removeItem(REDIRECT_URL_KEY + '_timestamp');
  }

  public saveUserEmail(email: string): void {
    window.sessionStorage.setItem(USER_EMAIL_KEY, email);
  }

  public getUserEmail(): string | null {
    return window.sessionStorage.getItem(USER_EMAIL_KEY);
  }

  public saveUserId(userId: string): void {
    window.sessionStorage.setItem(USER_ID_KEY, userId);
  }

  public getUserId(): string | null {
    return window.sessionStorage.getItem(USER_ID_KEY);
  }

  public saveIndividualId(id: string): void {
    window.sessionStorage.setItem(INDIVIDUAL_ID_KEY, id);
  }

  public getIndividualId(): string | null {
    return window.sessionStorage.getItem(INDIVIDUAL_ID_KEY);
  }

  public getUser(): any {
    let userInfo = this.getItem(USER_KEY);
    return userInfo ? JSON.parse(userInfo) : null;
  }

  public saveUser(user: any): void {
    window.sessionStorage.removeItem(USER_KEY);
    window.sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    this.setPrimaryEmailFromUser(user);
  }

  /**
   * Sets primaryEmail in sessionStorage from user profile (e.g. after Azure AD login).
   * Used by components that read sessionStorage.getItem('primaryEmail').
   */
  public setPrimaryEmailFromUser(user: { email?: string; username?: string } | null): void {
    const raw = user?.email ?? user?.username ?? '';
    const email = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (email) {
      window.sessionStorage.setItem(PRIMARY_EMAIL_KEY, email);
    }
  }

  public clear() {
    window.sessionStorage.removeItem(USER_KEY);
    window.sessionStorage.removeItem(AUTH_KEY);
    window.sessionStorage.removeItem(INDIVIDUAL_ID_KEY);
    window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    window.sessionStorage.removeItem(PRIMARY_EMAIL_KEY);
    // Don't remove the redirect URL when logging out
    // as it's needed for the redirect after login
  }

  public get isLoggedIn(): boolean {
    const user = window.sessionStorage.getItem(USER_KEY);
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();

    // User is considered logged in if they have both user info and at least a refresh token
    return !!(user && (token || refreshToken));
  }

  public get hasValidSession(): boolean {
    return this.getRefreshToken() !== null;
  }

  public forceRedirectToLogin(): void {
    // Clear all auth data
    this.clear();
    // Force navigate to login page using window.location to bypass routing issues
    window.location.href = '/account/login';
  }

  addItem(key: string, data: any) {
    window.sessionStorage.removeItem(key);
    window.sessionStorage.setItem(key, data);
  }

  getItem(key: string): string | null {
    return window.sessionStorage.getItem(key);
  }
}
